'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import styles from '../styles/PresaleProgress.module.css';
import { ClientPurchaseModal } from './ClientPurchaseModal';



const PresaleProgress = ({ onLoaded }) => {
  const progressRef = useRef(null);
  const valueRef = useRef(null);
  const intervalRef = useRef(null);
  const [presaleData, setPresaleData] = useState({
    currentAmount: 0,        // USD amount raised (from all sales: token + non-token)
    targetAmount: 1812000,    // USD target goal
    percentComplete: 0,
    tokenPrice: 0.012,
    tokensRemaining: 0,
    tokensSold: 0,           // Raw token count from the database
    totalOrders: 0,          // All orders (token + non-token)
    tokenOrders: 0,          // Token orders only
    tokenUSD: 0,             // USD from token sales only
    nonTokenUSD: 0,          // USD from non-token sales (NFTs, etc)
    saftUSD: 0,              // USD from SAFT investments
    presaleEndDate: null,    // End date of the presale
    isLoading: true,
    error: null
  });
  
  // Countdown timer state
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  // Set a reference to track API received end date  
  const presaleEndDateRef = useRef(null);
  
  // Change from showPurchaseFlow to showPurchaseModal
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  
  // Track wallet connection
  const [isConnected, setIsConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState(null);
  
  // Check wallet connection on mount
  useEffect(() => {
    const checkWalletConnection = async () => {
      try {
        // Check if AppKit is available and wallet is connected
        if (window.AppKit?.Web3Modal?.getAddress) {
          const address = await window.AppKit.Web3Modal.getAddress();
          if (address) {
            setWalletAddress(address);
            setIsConnected(true);
          }
        } else if (window.AppKit?.Web3Modal?.state?.address) {
          setWalletAddress(window.AppKit.Web3Modal.state.address);
          setIsConnected(true);
        }
      } catch (err) {
        console.error('Error checking wallet connection:', err);
      }
    };

    checkWalletConnection();

    // Listen for wallet connection/disconnection events
    const handleAccountsChanged = (event) => {
      const accounts = event.detail?.accounts || [];
      if (accounts.length > 0) {
        setWalletAddress(accounts[0]);
        setIsConnected(true);
      } else {
        setWalletAddress(null);
        setIsConnected(false);
      }
    };

    document.addEventListener('appkit:accountsChanged', handleAccountsChanged);
    
    return () => {
      document.removeEventListener('appkit:accountsChanged', handleAccountsChanged);
    };
  }, []);
  
  useEffect(() => {
    const fetchPresaleData = async () => {
      try {
        const response = await fetch('/api/presale/progress');
        
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'No error details available');
          throw new Error(`Failed to fetch presale data: ${response.status} ${response.statusText}\n${errorText}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          // Store the end date
          presaleEndDateRef.current = new Date(result.data.presaleEndDate);
          
          setPresaleData({
            currentAmount: result.data.currentAmount || 0,         // USD from all sales (token + non-token)
            targetAmount: result.data.targetAmount || 960000,      // In USD
            percentComplete: result.data.percentComplete || 0,
            tokenPrice: result.data.tokenPrice || 0.012,
            tokensRemaining: result.data.tokensRemaining || 0,
            tokensSold: result.data.tokensSold || 0,               // Raw token count
            totalOrders: result.data.totalOrders || 0,             // All orders
            tokenOrders: result.data.tokenOrders || 0,             // Token orders only
            tokenUSD: result.data.tokenUSD || 0,                   // USD from token sales
            nonTokenUSD: result.data.nonTokenUSD || 0,             // USD from non-token sales
            saftUSD: result.data.saftUSD || 0,                     // USD from SAFT investments
            presaleEndDate: result.data.presaleEndDate,            // Store string version
            isLoading: false,
            error: null
          });
          
          // Call the onLoaded callback if provided
          if (onLoaded && typeof onLoaded === 'function') {
            onLoaded();
          }
        } else {
          throw new Error(result.message || result.error || 'Failed to fetch presale data');
        }
      } catch (error) {
        setPresaleData(prev => ({
          ...prev,
          isLoading: false,
          error: error.message || 'An unexpected error occurred'
        }));
        
        // Call onLoaded even on error to prevent blocking animations
        if (onLoaded && typeof onLoaded === 'function') {
          onLoaded();
        }
      }
    };
    
    fetchPresaleData();
    
    // Set up a polling interval to refresh data
    const intervalId = setInterval(fetchPresaleData, 5 * 60 * 1000); // Every 5 minutes
    
    return () => clearInterval(intervalId);
  }, [onLoaded]);
  
  // Update countdown timer based on API end date
  useEffect(() => {
    // Only start the countdown after we receive the end date from API
    if (!presaleEndDateRef.current) return;
    
    const calculateTimeLeft = () => {
      const difference = presaleEndDateRef.current - new Date();
      
      if (difference > 0) {
        // Calculate time units
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);
        
        setCountdown({ days, hours, minutes, seconds });
      } else {
        // Timer has ended
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    // Calculate immediately
    calculateTimeLeft();
    
    // Set up timer
    const timer = setInterval(calculateTimeLeft, 1000);
    
    // Clean up
    return () => clearInterval(timer);
  }, [presaleData.presaleEndDate]); // Re-run when the end date changes
  
  useEffect(() => {
    const progressBar = progressRef.current;
    const valueDisplay = valueRef.current;
    
    if (!progressBar || !valueDisplay || presaleData.isLoading) return;
    
    // Add small delay to ensure Hero component animations start first
    setTimeout(() => {
      // Animate the progress bar filling up
      gsap.fromTo(
        progressBar,
        { width: '0%' },
        {
          width: `${presaleData.percentComplete}%`,
          duration: 2,
          ease: 'power2.out',
          onUpdate: function() {
            // Update the marker position to match the progress bar's current width
            const markerElement = document.querySelector(`.${styles.progressMarker}`);
            if (markerElement) {
              // Get the current width value directly from GSAP animation
              markerElement.style.left = this.targets()[0].style.width;
            }
          }
        }
      );
      
      // Create a counter to track the animated value
      let startValue = 0;
      const endValue = presaleData.currentAmount; // This includes token + NFT + SAFT sales
      const duration = 2; // seconds
      const stepTime = 30; // Update every 30ms
      const totalSteps = (duration * 1000) / stepTime;
      const stepValue = (endValue - startValue) / totalSteps;
      
      // Function to format currency properly
      const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      };
      
      // Set initial value
      valueDisplay.textContent = formatCurrency(startValue);
      
      // Set up the animation
      intervalRef.current = setInterval(() => {
        startValue += stepValue;
        
        if (startValue >= endValue) {
          startValue = endValue;
          clearInterval(intervalRef.current);
        }
        
        valueDisplay.textContent = formatCurrency(startValue);
      }, stepTime);
    }, 200); // 200ms delay to ensure Hero animations start first
    
    // Clean up the interval on component unmount or when dependencies change
    return () => {
      // We need to ensure any intervals are properly cleared
      if (intervalRef.current) {
         clearInterval(intervalRef.current);
      }
    };
  }, [presaleData.isLoading, presaleData.currentAmount, presaleData.percentComplete]);
  
  // Add a debug function
  const forceGoToStep2 = () => {
    if (process.env.NODE_ENV === 'development') {
      setShowPurchaseModal(true);
    }
  };
  
  if (presaleData.error) {
    return (
      <div className={styles.presaleContainer}>
        <div className={styles.errorMessage}>
          Unable to load presale data. Please try again later.
        </div>
      </div>
    );
  }
  
  if (presaleData.isLoading) {
    return (
      <div className={`${styles.presaleContainer} ${styles.loading}`}>
        {/* No loading message text needed - we'll keep the container with proper dimensions */}
      </div>
    );
  }
  
  
  // Format countdown display
  const formatTimeUnit = (value) => {
    return value.toString().padStart(2, '0');
  };
  
  // Calculate if we're in the final day for extra urgency
  const isInFinalDay = countdown.days === 0 && countdown.hours < 24;
  
  return (
    <div className={styles.presaleContainer}>
      <div className={styles.header}>
        <h2>$GAM Elite Presale - Round 1:</h2>
        <div className={styles.figures}>
          <span className={styles.raised}>
            $<span ref={valueRef}>0.00</span> USD
          </span>
          <span className={styles.target}>of ${presaleData.targetAmount.toLocaleString()} USD</span>
        </div>
      </div>
      
      <div className={styles.progressWrapper}>
        <div className={styles.progressBackground}>
          <div 
            ref={progressRef} 
            className={styles.progressBar}
            style={{ width: '0%' }}
          ></div>
          <div 
            className={styles.progressMarker}
            style={{ 
              left: '0%',
              backgroundImage: 'url(https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/93a2fe1a-6a0c-48ac-af1d-70ae659fe200/public)'
            }}
          ></div>
        </div>
        <div className={styles.percentComplete}>{presaleData.percentComplete.toFixed(1)}%</div>
      </div>
      
      <div className={`${styles.countdownContainer} ${isInFinalDay ? styles.finalDay : ''}`}>
        <div className={styles.countdownHeader}>
          <span className={styles.timerIcon}>⏱️</span> 
          {isInFinalDay ? 'FINAL COUNTDOWN!' : 'Time Remaining:'}
        </div>
        <div className={styles.countdownTimer}>
          <div className={styles.countdownUnit}>
            <div className={styles.countdownValue}>{formatTimeUnit(countdown.days)}</div>
            <div className={styles.countdownLabel}>Days</div>
          </div>
          <div className={styles.countdownSeparator}>:</div>
          <div className={styles.countdownUnit}>
            <div className={styles.countdownValue}>{formatTimeUnit(countdown.hours)}</div>
            <div className={styles.countdownLabel}>Hours</div>
          </div>
          <div className={styles.countdownSeparator}>:</div>
          <div className={styles.countdownUnit}>
            <div className={styles.countdownValue}>{formatTimeUnit(countdown.minutes)}</div>
            <div className={styles.countdownLabel}>Mins</div>
          </div>
          <div className={styles.countdownSeparator}>:</div>
          <div className={styles.countdownUnit}>
            <div className={styles.countdownValue}>{formatTimeUnit(countdown.seconds)}</div>
            <div className={styles.countdownLabel}>Secs</div>
          </div>
        </div>
      </div>
      
      <div className={styles.tokenInfo}>
        <div className={styles.infoItem}>
          <span className={styles.label}>Token Price:</span>
          <span className={styles.value}>${presaleData.tokenPrice} USD</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.label}>Tokens Sold:</span>
          <span className={styles.value}>{Math.round(presaleData.tokensSold).toLocaleString()}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.label}>Tokens Remaining:</span>
          <span className={styles.value}>{Math.round(presaleData.tokensRemaining).toLocaleString()}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.label}>Total Orders:</span>
          <span className={styles.value}>{presaleData.totalOrders}</span>
        </div>
      </div>
      
      <div className={styles.cta}>
        <button 
          className={styles.buyButton}
          onClick={() => setShowPurchaseModal(true)}
        >
          {isInFinalDay ? 'SECURE YOUR TOKENS NOW!' : 'Buy GAM Tokens Now'}
          <span className={styles.buttonGlow}></span>
        </button>
      </div>
      
      
      
      <ClientPurchaseModal 
        isOpen={showPurchaseModal} 
        onClose={() => setShowPurchaseModal(false)} 
        projectUsdtWalletAddress={process.env.NEXT_PUBLIC_MOONPAY_PROJECT_USDC_WALLET_ADDRESS} 
      />
    </div>
  );
};

export default PresaleProgress; 
